import { useState, useMemo } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  FileText, Inbox, Brain, CheckCircle, ThumbsUp, 
  CreditCard, Archive as ArchiveIcon, ChevronRight, ChevronLeft,
  ArrowRight, ArrowLeft, Info, TrendingUp, Clock
} from "lucide-react";
import { useBillQueue } from "@/hooks/useBillQueue";
import { StageTabs } from "@/components/StageTabs";
import { ManualReviewEditor } from "@/components/tum/ManualReviewEditor";
import type { BillStage } from "@/lib/stage-utils";
import { cn } from "@/lib/utils";

// Map TUM 7-stage structure to existing BillQueue stages with color themes
const stages = [
  { 
    id: 'ingestion' as BillStage, 
    name: 'Ingestion', 
    description: 'Bills uploaded and awaiting processing',
    icon: Inbox, 
    queueStage: 'intake' as BillStage,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
    borderColor: 'border-blue-500',
    activeBg: 'bg-blue-100 dark:bg-blue-900/30'
  },
  { 
    id: 'processing' as BillStage, 
    name: 'Processing', 
    description: 'AI extraction and data parsing in progress',
    icon: Brain, 
    queueStage: 'processing' as BillStage,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50 dark:bg-purple-950/20',
    borderColor: 'border-purple-500',
    activeBg: 'bg-purple-100 dark:bg-purple-900/30'
  },
  { 
    id: 'manual_review' as BillStage, 
    name: 'Manual Review', 
    description: 'Bills requiring human verification',
    icon: FileText, 
    queueStage: 'manual_review' as BillStage,
    iconColor: 'text-orange-600',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
    borderColor: 'border-orange-500',
    activeBg: 'bg-orange-100 dark:bg-orange-900/30'
  },
  { 
    id: 'validation' as BillStage, 
    name: 'Validation', 
    description: 'Business rules and quality checks',
    icon: CheckCircle, 
    queueStage: 'validation' as BillStage,
    iconColor: 'text-cyan-600',
    bgColor: 'bg-cyan-50 dark:bg-cyan-950/20',
    borderColor: 'border-cyan-500',
    activeBg: 'bg-cyan-100 dark:bg-cyan-900/30'
  },
  { 
    id: 'approval' as BillStage, 
    name: 'Approval', 
    description: 'Pending authorization for payment',
    icon: ThumbsUp, 
    queueStage: 'approval' as BillStage,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    borderColor: 'border-green-500',
    activeBg: 'bg-green-100 dark:bg-green-900/30'
  },
  { 
    id: 'payment_ready' as BillStage, 
    name: 'Payment Ready', 
    description: 'Approved and ready for payment processing',
    icon: CreditCard, 
    queueStage: 'payment_ready' as BillStage,
    iconColor: 'text-emerald-600',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/20',
    borderColor: 'border-emerald-600',
    activeBg: 'bg-emerald-100 dark:bg-emerald-900/30'
  },
  { 
    id: 'archive' as BillStage, 
    name: 'Archive', 
    description: 'Completed bills stored for reference',
    icon: ArchiveIcon, 
    queueStage: 'archive' as BillStage,
    iconColor: 'text-gray-600',
    bgColor: 'bg-gray-50 dark:bg-gray-950/20',
    borderColor: 'border-gray-500',
    activeBg: 'bg-gray-100 dark:bg-gray-900/30'
  },
];

export default function TumBillJourney() {
  const [activeStage, setActiveStage] = useState<BillStage>('intake');
  const [selectedBill, setSelectedBill] = useState<any>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  
  const { 
    intakeItems,
    processingItems,
    manualReviewItems,
    validationItems,
    approvalItems,
    paymentReadyItems,
    archiveItems,
    isLoading
  } = useBillQueue();

  // Map stage arrays for easy access
  const stageData: Record<BillStage, any[]> = {
    intake: intakeItems,
    extraction: [],
    processing: processingItems,
    manual_review: manualReviewItems,
    validation: validationItems,
    approval: approvalItems,
    payment_ready: paymentReadyItems,
    archive: archiveItems,
  };

  // Stage counts
  const stageCounts = {
    intake: intakeItems.length,
    processing: processingItems.length,
    manual_review: manualReviewItems.length,
    validation: validationItems.length,
    approval: approvalItems.length,
    payment_ready: paymentReadyItems.length,
    archive: archiveItems.length,
  };

  // Calculate total bills and workflow metrics
  const totalBills = useMemo(() => {
    return Object.values(stageCounts).reduce((sum, count) => sum + count, 0);
  }, [stageCounts]);

  const activeStageIndex = stages.findIndex(s => s.queueStage === activeStage);
  const currentStage = stages[activeStageIndex];

  const handleNextStage = () => {
    if (activeStageIndex < stages.length - 1) {
      setActiveStage(stages[activeStageIndex + 1].queueStage);
    }
  };

  const handlePreviousStage = () => {
    if (activeStageIndex > 0) {
      setActiveStage(stages[activeStageIndex - 1].queueStage);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault();
      handleNextStage();
    } else if (e.key === 'ArrowLeft' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault();
      handlePreviousStage();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]" onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Compact Header */}
      <div className="flex-shrink-0 mb-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Bill Journey</h1>
            <p className="text-sm text-muted-foreground">Track bills through the processing pipeline</p>
          </div>
          {/* Compact Stats */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200">
              <FileText className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold">{totalBills}</span>
              <span className="text-xs text-muted-foreground">Total</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200">
              <Clock className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-semibold">
                {stageCounts.processing + stageCounts.manual_review + stageCounts.validation}
              </span>
              <span className="text-xs text-muted-foreground">Active</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200">
              <CreditCard className="h-4 w-4 text-green-600" />
              <span className="text-sm font-semibold text-green-600">{stageCounts.payment_ready}</span>
              <span className="text-xs text-muted-foreground">Ready</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stage Navigation with Progress Indicator - Compact */}
      <Card className="border-l-4 border-l-primary/50 flex-shrink-0">
        <CardContent className="p-3">
          {/* Compact Progress Bar and Navigation */}
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Stage {activeStageIndex + 1} of {stages.length}</span>
                <span className="text-xs font-medium">
                  {Math.round(((activeStageIndex + 1) / stages.length) * 100)}% Complete
                </span>
              </div>
              <Progress 
                value={((activeStageIndex + 1) / stages.length) * 100} 
                className="h-1.5"
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousStage}
                disabled={activeStageIndex === 0}
                className="h-7 w-7 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextStage}
                disabled={activeStageIndex === stages.length - 1}
                className="h-7 w-7 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Stage Tabs with Enhanced Styling - Compact */}
      <Tabs value={activeStage} onValueChange={(value) => setActiveStage(value as BillStage)}>
            <div className="border-b border-border bg-muted/30 rounded-t-lg p-1.5">
              <TabsList className="w-full justify-start overflow-x-auto flex-nowrap h-auto bg-transparent p-0 gap-1.5">
                {stages.map((stage, index) => {
            const Icon = stage.icon;
            const count = stageCounts[stage.queueStage] || 0;
            const isActive = activeStage === stage.queueStage;
                  const isCompleted = index < activeStageIndex;
                  
            return (
              <TabsTrigger 
                key={stage.id} 
                value={stage.queueStage}
                      className={cn(
                        "flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 transition-all rounded-md border-2 px-2.5 py-1.5 text-xs",
                        isActive
                          ? `${stage.activeBg} ${stage.borderColor} border-2 shadow-sm font-semibold`
                          : "border-transparent bg-background hover:bg-muted",
                        isCompleted && !isActive && "opacity-75"
                      )}
              >
                      <div className={cn(
                        "p-1 rounded",
                        isActive ? stage.bgColor : "bg-muted"
                      )}>
                        <Icon className={cn(
                          "h-3.5 w-3.5",
                          isActive ? stage.iconColor : "text-muted-foreground"
                        )} />
                      </div>
                      <span className="font-medium">{stage.name}</span>
                <Badge 
                        variant={isActive ? "default" : "secondary"}
                        className={cn(
                          "ml-1 font-semibold text-xs h-5 px-1.5",
                          isActive && `${stage.bgColor} ${stage.iconColor} border ${stage.borderColor}`
                        )}
                >
                  {count}
                </Badge>
                      {isCompleted && !isActive && (
                        <CheckCircle className="h-3 w-3 text-green-600 ml-0.5" />
                      )}
              </TabsTrigger>
            );
          })}
        </TabsList>
            </div>

            {/* Stage Content with Current Stage Info - Scrollable */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {stages.map((stage) => {
                const isActive = activeStage === stage.queueStage;
                return (
                  <TabsContent 
                    key={stage.id} 
                    value={stage.queueStage} 
                    className="flex-1 overflow-y-auto mt-2 space-y-2"
                  >
                    {isActive && (
                      <Card className={cn(
                        "border-l-4 flex-shrink-0",
                        stage.borderColor,
                        stage.bgColor
                      )}>
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "p-2 rounded-lg",
                              stage.bgColor
                            )}>
                              <stage.icon className={cn("h-4 w-4", stage.iconColor)} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <CardTitle className="text-base font-semibold">
                                  {stage.name}
                                </CardTitle>
                                <Badge variant="outline" className={cn("border text-xs h-5", stage.borderColor)}>
                                  {stageCounts[stage.queueStage]} bills
                                </Badge>
                              </div>
                              <CardDescription className="text-xs mt-0.5">
                                {stage.description}
                              </CardDescription>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    <div className="flex-1 min-h-0">
            <StageTabs stage={stage.queueStage} />
                    </div>
          </TabsContent>
                );
              })}
            </div>
      </Tabs>
        </CardContent>
      </Card>

      {/* Compact Keyboard Shortcuts Hint */}
      <div className="flex-shrink-0 text-xs text-muted-foreground flex items-center gap-1.5 px-2 py-1">
        <Info className="h-3 w-3" />
        <span>Use <kbd className="px-1 py-0.5 bg-background border rounded text-[10px]">←</kbd> <kbd className="px-1 py-0.5 bg-background border rounded text-[10px]">→</kbd> to navigate</span>
      </div>

      <ManualReviewEditor
        bill={selectedBill}
        isOpen={isEditorOpen}
        onClose={() => {
          setIsEditorOpen(false);
          setSelectedBill(null);
        }}
        onAccept={async (updatedData) => {
          console.log('Accepted:', updatedData);
          setIsEditorOpen(false);
        }}
      />
    </div>
  );
}
